import { useQuery } from "@tanstack/react-query"
import { tintVTOService } from "services/tintvto"
import { useAlert } from "providers/AlertProvider"
import { useTranslation } from "react-i18next"
import { useAppSelector } from "hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"

export const ANALYTICS_QUERY_KEY = (
  merchantId: string,
  days?: string,
  dateStart?: string,
  dateEnd?: string
) => [
  "analytics",
  {
    merchantId,
    days,
    dateStart,
    dateEnd,
  },
]

export type AnalyticsQueryParams = {
  merchantId: string
  days?: string
  dateStart?: string
  dateEnd?: string
}

export const useAnalyticsQuery = ({
  merchantId,
  days,
  dateStart,
  dateEnd,
}: AnalyticsQueryParams) => {
  const { showAlert } = useAlert()
  const { t } = useTranslation()
  const userInfo = useAppSelector(selectUserInfo)

  const isAdmin = userInfo?.groups?.includes("Admin") || false

  return useQuery({
    queryKey: ANALYTICS_QUERY_KEY(merchantId, days, dateStart, dateEnd),
    queryFn: async () => {
      try {
        const { data } = await tintVTOService.getAnalytics(merchantId, days, dateStart, dateEnd)

        const analyticsString = data?.analytics
        if (typeof analyticsString === "string")
          try {
            const parsedData = JSON.parse(analyticsString)
            return parsedData
          } catch (parseError) {
            console.error("Failed to parse analytics JSON:", parseError)
            throw new Error("Invalid analytics data format")
          }

        return analyticsString
      } catch (error) {
        showAlert({ error: true, text: t("analytics.fetchError") })
        throw error
      }
    },
    enabled: !!merchantId && (isAdmin || !!userInfo?.merchantId),
  })
}
